import {useEffect, useState} from "react";

import baseUrl from "../../../baseUrl";
import {requester} from "../../../services/requester";
import {
    CSV_DELIMITER, DEALS_IMPORT_COLUMNS, DRAWINGS_IMPORT_COLUMNS,
    decodeFileContent, downloadCsvTemplate, parseDealsImportCsv,
    parseDrawingsImportCsv,
} from "../../../utils/csv";
import {ImportBatches} from "./ImportBatches";
import {ImportReport} from "./ImportReport";
import {UnresolvedNames} from "./UnresolvedNames";
import {useConfirm} from "../../../contexts/ConfirmContext";

// Л — масов внос от стари бази. Един компонент за двата файла: разликите са
// само в колоните, ендпойнтите и надписите. Ако бяха два компонента, първата
// поправка щеше да влезе само в единия.
//
// Потокът е три стъпки и средната е задължителна: файл → ПРЕГЛЕД → запис.
// Прегледът не пише нищо; записът повтаря същите проверки на сървъра.

const CONFIG = {
    deals: {
        title: "Доброволни замени (стари бази)",
        columns: DEALS_IMPORT_COLUMNS,
        templateName: "shablon-dobrovolni-zameni.csv",
        parse: parseDealsImportCsv,
        previewUrl: "/import/deals/preview",
        commitUrl: "/import/deals/commit",
        what: "замени",
        help: (
            <>
                Колони: <code>Даващ; Взимащ; За село; Сума; Година; ЕКТТ</code>.
                Даващият и взимащият са <strong>по име</strong> — приложението ги
                свързва с булстат от вече качените данни за същото землище и
                година. Повтарящите се редове (един и същ даващ, взимащ и „За
                село“) се <strong>сумират</strong>.
            </>
        ),
    },
    drawings: {
        title: "Изчертани площи (стари бази)",
        columns: DRAWINGS_IMPORT_COLUMNS,
        templateName: "shablon-izchertani-ploshti.csv",
        parse: parseDrawingsImportCsv,
        previewUrl: "/import/drawings/preview",
        commitUrl: "/import/drawings/commit",
        what: "изчертани площи",
        help: (
            <>
                Колони: <code>Ползвател; Масив; МПО Номер; Площ; Година; ЕКТТ</code>.
                Ползвателят е <strong>по име</strong>. Културата не се внася —
                въвежда се после в приложението.
            </>
        ),
    },
};

export const BulkImport = ({kind}) => {
    const config = CONFIG[kind];
    const request = requester();
    const confirm = useConfirm();

    const [file, setFile] = useState(null);
    const [rows, setRows] = useState(null);
    const [report, setReport] = useState(null);
    const [mapping, setMapping] = useState({});
    const [mappingDirty, setMappingDirty] = useState(false);
    const [replace, setReplace] = useState(false);
    const [error, setError] = useState("");
    const [busy, setBusy] = useState(false);
    const [result, setResult] = useState(null);
    const [batches, setBatches] = useState([]);

    const loadBatches = () => {
        request.get(`${baseUrl()}/import/batches`)
            .then(setBatches)
            .catch(() => setBatches([]));
    };

    useEffect(() => {
        loadBatches();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const reset = () => {
        setRows(null);
        setReport(null);
        setMapping({});
        setMappingDirty(false);
        setError("");
        setResult(null);
    };

    const handleFileChange = (e) => {
        reset();
        setFile(e.target.files?.[0] || null);
    };

    const handleParse = () => {
        if (!file) return setError("Не е избран файл.");
        setError("");
        setResult(null);

        const reader = new FileReader();
        reader.onload = ({target}) => {
            // Файловете от старата система са Windows-1251 — decodeFileContent
            // разпознава кодировката, не се иска преобразуване от потребителя.
            const parsed = config.parse(decodeFileContent(target.result));
            if (parsed.error) {
                setError(parsed.error);
                setRows(null);
                return;
            }
            setRows(parsed.rows);
            runPreview(parsed.rows, {});
        };
        reader.readAsArrayBuffer(file);
    };

    // `opts` е за отметката: `setState` е асинхронен, тоест ако прегледът
    // четеше `replace` от състоянието веднага след клика, щеше да прати
    // СТАРАТА стойност и екранът да покаже обратното на отметнатото.
    const body = (theRows, theMapping, opts = {}) => ({
        rows: theRows,
        mapping: theMapping,
        replace: opts.replace !== undefined ? opts.replace : replace,
        filename: file?.name || "",
    });

    const runPreview = (theRows, theMapping, opts = {}) => {
        setBusy(true);
        setError("");
        request.post(`${baseUrl()}${config.previewUrl}`, body(theRows, theMapping, opts))
            .then(data => {
                setReport(data);
                setMappingDirty(false);
            })
            .catch(err => setError(err?.message || "Грешка при прегледа."))
            .finally(() => setBusy(false));
    };

    const onMappingChange = (name, value) => {
        setMapping(prev => {
            const next = {...prev};
            if (value === null) delete next[name]; else next[name] = value;
            return next;
        });
        setMappingDirty(true);
    };

    const handleCommit = async () => {
        if (!rows || !report?.ok) return;
        if (replace && !await confirm({
            title: "Да се заместят ли съществуващите данни?",
            message: `Първо ще бъдат изтрити вече записаните данни за `
                + `${report.year} г. в ${report.settlement_name}, после ще се запише файлът.`,
            detail: `За изтриване: ${report.totals?.will_delete || 0} записа`,
            confirmText: "Да, замести",
        })) {
            return;
        }
        setBusy(true);
        setError("");
        request.post(`${baseUrl()}${config.commitUrl}`, body(rows, mapping))
            .then(data => {
                setResult(data);
                setReport(null);
                setRows(null);
                setFile(null);
                setMapping({});
                const input = document.getElementById(`import-${kind}`);
                if (input) input.value = "";
                loadBatches();
            })
            .catch(err => setError(err?.message || "Грешка при записа."))
            .finally(() => setBusy(false));
    };

    const handleUndo = async (batch) => {
        if (!await confirm({
            title: "Да се отмени ли този внос?",
            message: batch.replaced
                ? "Записаното от файла ще бъде изтрито. Данните, които този внос "
                  + "е заместил, НЕ се възстановяват."
                : "Записаното от файла ще бъде изтрито.",
            detail: `${batch.filename || "—"} · ${batch.rows_written} записа`,
            confirmText: "Да, отмени вноса",
        })) {
            return;
        }
        setBusy(true);
        request.delete(`${baseUrl()}/import/batches/${batch.id}`)
            .then(data => {
                setResult(null);
                alert(data.message);
                loadBatches();
            })
            .catch(err => setError(err?.message || "Грешка при отмяната."))
            .finally(() => setBusy(false));
    };

    return (
        <div className="mt-4" style={{
            border: "1px solid var(--app-gold)",
            borderRadius: "6px",
            padding: "16px 20px",
        }}>
            <h4 style={{textDecoration: "underline"}}>{config.title}</h4>
            <p className="text-muted" style={{fontSize: "0.9rem"}}>{config.help}</p>
            <p className="text-muted" style={{fontSize: "0.9rem"}}>
                Един файл е за <strong>едно землище и една година</strong>. Първо
                качете <strong>правното основание</strong> за тях — то е
                източникът на връзката име ↔ булстат.
            </p>

            <div className="row g-2 align-items-center">
                <div className="col-12 col-md-7">
                    <input id={`import-${kind}`} type="file" className="form-control"
                           accept=".csv,text/csv" onChange={handleFileChange}/>
                </div>
                <div className="col-auto">
                    <button type="button" className="btn btn-outline-secondary btn-sm"
                            onClick={() => downloadCsvTemplate(
                                config.columns.join(CSV_DELIMITER), config.templateName)}>
                        ⬇ Свали шаблон (.csv)
                    </button>
                </div>
            </div>

            <div className="form-check mt-2">
                <input className="form-check-input" type="checkbox" id={`replace-${kind}`}
                       checked={replace} onChange={(e) => {
                           setReplace(e.target.checked);
                           if (rows) runPreview(rows, mapping, {replace: e.target.checked});
                       }}/>
                <label className="form-check-label" htmlFor={`replace-${kind}`}>
                    Замести съществуващите за тази година и землище
                    <span className="text-muted"> — изтрива ги, преди да запише файла</span>
                </label>
            </div>

            {file && !report && (
                <button type="button" className="btn btn-secondary mt-2"
                        disabled={busy} onClick={handleParse}>
                    {busy ? "Обработва се…" : "Прегледай"}
                </button>
            )}

            {error && (
                <div className="alert alert-danger mt-3 mb-0">{error}</div>
            )}

            <ImportReport report={report} kind={kind}/>

            {report && (
                <UnresolvedNames unresolved={report.unresolved} mapping={mapping}
                                 onChange={onMappingChange}/>
            )}

            {report && (
                <div className="d-flex gap-2 mt-3 flex-wrap">
                    {mappingDirty && (
                        <button type="button" className="btn btn-primary"
                                disabled={busy} onClick={() => runPreview(rows, mapping)}>
                            Приложи съпоставките и прегледай отново
                        </button>
                    )}
                    <button type="button" className="btn btn-success"
                            disabled={busy || !report.ok || mappingDirty}
                            onClick={handleCommit}>
                        Запиши в базата
                    </button>
                    <button type="button" className="btn btn-outline-secondary"
                            disabled={busy} onClick={() => {
                                reset();
                                setFile(null);
                                const input = document.getElementById(`import-${kind}`);
                                if (input) input.value = "";
                            }}>
                        Откажи
                    </button>
                </div>
            )}

            {result && (
                <div className="alert alert-success mt-3">
                    <strong>Готово.</strong> Записани {result.written} {config.what}
                    {result.deleted > 0 && ` (изтрити при заместване: ${result.deleted} реда)`}.
                    {" "}Ако нещо не е наред, отменете вноса от списъка по-долу.
                </div>
            )}

            <ImportBatches batches={batches} kind={kind} onUndo={handleUndo} busy={busy}/>
        </div>
    );
};
